import React, { memo, useRef } from 'react';
import { useToggleState } from '@react-stately/toggle';
import { useLabel } from '@react-aria/label';
import { AriaCheckboxProps, useCheckbox } from '@react-aria/checkbox';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useFocusRing } from '../useFocusRing';
import { cn } from '../cn';
import TextfieldHelperText from '../TextField/TextfieldHelperText';
import { Typography } from '../Typography/Typography';
import { IconCheckboxChecked, IconCheckboxUnchecked } from '../Icons';

export type CheckboxAriaProps = React.PropsWithChildren<{
  className?: string,
  label?: string | React.ReactNode,
  errorMessage?: string,
  description?: string,
} & AriaCheckboxProps>;

export const Checkbox = (baseProps: CheckboxAriaProps) => {
  const ref = useRef(null);
  const isSelected = baseProps.isSelected || (baseProps as any).checked || baseProps.value || false;
  if ((baseProps as any).checked !== undefined || baseProps.value !== undefined) {
    console.warn('Checkbox: checked and value props are deprecated, please use isSelected instead.');
  }
  const validationState = baseProps.isInvalid ? 'invalid' : baseProps.errorMessage ? 'invalid' : undefined;
  const finalProps = Object.assign({}, baseProps, { isSelected, validationState });
  const state = useToggleState(finalProps);
  const { inputProps } = useCheckbox(Object.assign({ 'aria-label': finalProps.label }, finalProps), state, ref);
  const { labelProps, fieldProps } = useLabel(finalProps);
  const { isFocusVisible, focusProps } = useFocusRing();

  const CheckboxSVG = state.isSelected ? IconCheckboxChecked : IconCheckboxUnchecked;
  const { errorMessage, description } = baseProps;
  
  return (
    <div className={finalProps.className}>
      <label
        {...labelProps}
        className={cn('cursor-pointer', inputProps.disabled && 'opacity-50 cursor-not-allowed select-none')}
      >
        <Typography size='sm' className={cn(
          'flex items-center gap-1',
          state.isSelected ? 'font-semibold' : 'font-normal',
        )}>
          <VisuallyHidden>
            <input
              {...inputProps}
              {...fieldProps}
              {...focusProps}
              ref={ref}
            />
          </VisuallyHidden>
          <CheckboxSVG
            aria-hidden="true"
            className={cn(
              'size-6 min-w-6 min-h-6',
              errorMessage && 'text-error',
            )}
          />
          {baseProps.label}
        </Typography>
        <div>
          {(errorMessage || description) ? (
            <TextfieldHelperText status={!errorMessage ? 'neutral' : 'error'}>
              {errorMessage || description}
            </TextfieldHelperText>
          ) : null}
        </div>
      </label>
    </div>
  );
};